/**
 * ── Trial Orientation Config ──
 * Defines goals, quick-wins, activation milestones, and demo/clean mode
 * for the lightweight trial-first experience.
 */

export type TrialMode = "demo" | "clean" | null;

export interface TrialGoal {
  id: string;
  label: string;
  description: string;
  icon: string;
  quickWins: TrialQuickWin[];
}

export interface TrialQuickWin {
  key: string;
  label: string;
  description: string;
  route: string;
  icon: string;
}

export interface TrialMilestone {
  key: string;
  label: string;
  description: string;
  autoDetect?: { table: string; minCount: number };
}

/** Trial orientation goals — what the user wants to accomplish first (org trials) */
export const TRIAL_GOALS: TrialGoal[] = [
  {
    id: "events",
    label: "Run events better",
    description: "Create, promote, and manage events with registration and check-in",
    icon: "CalendarDays",
    quickWins: [
      { key: "create_event", label: "Create your first event", description: "Set up an event with details, pricing, and registration", route: "/events", icon: "CalendarDays" },
      { key: "preview_registration", label: "Preview the registration page", description: "See what attendees experience when they register", route: "/events", icon: "Eye" },
      { key: "explore_checkin", label: "Explore QR check-in", description: "See how check-in works at your events", route: "/events", icon: "ScanLine" },
    ],
  },
  {
    id: "engagement",
    label: "Improve member engagement",
    description: "Track interactions, send communications, and drive participation",
    icon: "Users",
    quickWins: [
      { key: "add_members", label: "Add a few members", description: "Import or manually add members to your directory", route: "/members", icon: "UserPlus" },
      { key: "send_communication", label: "Draft a communication", description: "Create your first email to members", route: "/communications", icon: "Mail" },
      { key: "view_member_profile", label: "View a member profile", description: "See the 360° view of a member", route: "/members", icon: "Eye" },
    ],
  },
  {
    id: "reporting",
    label: "Understand member value",
    description: "See dashboards, reporting, and value visibility tools",
    icon: "BarChart3",
    quickWins: [
      { key: "explore_dashboard", label: "Explore the dashboard", description: "Review your organization's key metrics", route: "/", icon: "LayoutDashboard" },
      { key: "view_retention", label: "View retention insights", description: "Understand renewal risks and member health", route: "/retention", icon: "TrendingUp" },
      { key: "generate_report", label: "Preview a report", description: "See what board-ready reporting looks like", route: "/reports", icon: "FileText" },
    ],
  },
  {
    id: "member_experience",
    label: "Explore the member experience",
    description: "See what your members see — portal, directory, and self-service",
    icon: "Globe",
    quickWins: [
      { key: "preview_portal", label: "Preview the member portal", description: "See the self-service experience members get", route: "/member-portal-preview", icon: "Globe" },
      { key: "explore_directory", label: "Explore the member directory", description: "See how members find and connect with each other", route: "/member-portal-preview/directory", icon: "Search" },
      { key: "add_members_portal", label: "Add members to see it live", description: "Add a few members to populate the portal", route: "/members", icon: "UserPlus" },
    ],
  },
  {
    id: "sponsorships",
    label: "Explore sponsorships and revenue",
    description: "Track sponsors, manage inventory, and grow non-dues revenue",
    icon: "DollarSign",
    quickWins: [
      { key: "explore_sponsorships", label: "Explore sponsorship tools", description: "See how to manage sponsors and packages", route: "/sponsorships", icon: "Handshake" },
      { key: "view_billing", label: "View billing overview", description: "Understand billing, invoicing, and payment tools", route: "/billing", icon: "CreditCard" },
      { key: "create_offer", label: "Create a billable offer", description: "Set up a product, event ticket, or sponsorship package", route: "/billing", icon: "Package" },
    ],
  },
  {
    id: "association",
    label: "Explore association / network tools",
    description: "Multi-chapter governance, benchmarking, and network management",
    icon: "Building2",
    quickWins: [
      { key: "explore_association", label: "Explore network management", description: "See multi-organization governance and oversight tools", route: "/association", icon: "Building2" },
      { key: "view_benchmarking", label: "View benchmarking", description: "Compare performance across organizations", route: "/association", icon: "BarChart3" },
      { key: "explore_exchange", label: "Explore the exchange", description: "See how organizations share content and resources", route: "/exchange", icon: "ArrowLeftRight" },
    ],
  },
];

/** Association-specific trial goals — shown when trial_type = "association" */
export const ASSOCIATION_TRIAL_GOALS: TrialGoal[] = [
  {
    id: "network_overview",
    label: "Explore network oversight",
    description: "See how to manage multiple chapters/orgs from one dashboard",
    icon: "Building2",
    quickWins: [
      { key: "explore_association_dashboard", label: "Explore the association dashboard", description: "See network-wide metrics and chapter health", route: "/association", icon: "Building2" },
      { key: "view_child_orgs", label: "View member organizations", description: "See how chapters and member orgs are structured", route: "/association", icon: "Users" },
      { key: "explore_shared_settings", label: "Explore shared settings", description: "See how policies and branding flow to chapters", route: "/association", icon: "Eye" },
    ],
  },
  {
    id: "cross_org_reporting",
    label: "Understand network performance",
    description: "Benchmarking, cross-org reporting, and aggregate analytics",
    icon: "BarChart3",
    quickWins: [
      { key: "view_benchmarking", label: "View benchmarking", description: "Compare performance across chapters", route: "/association", icon: "BarChart3" },
      { key: "explore_aggregate_dashboard", label: "Explore aggregate reporting", description: "See combined metrics across all organizations", route: "/", icon: "LayoutDashboard" },
      { key: "preview_board_report", label: "Preview a board report", description: "See what network-level reporting looks like", route: "/reports", icon: "FileText" },
    ],
  },
  {
    id: "content_exchange",
    label: "Explore the content exchange",
    description: "Share events, offers, and resources across organizations",
    icon: "ArrowLeftRight",
    quickWins: [
      { key: "explore_exchange", label: "Explore the exchange", description: "See how organizations share content", route: "/exchange", icon: "ArrowLeftRight" },
      { key: "view_shared_events", label: "View shared events", description: "See events distributed across the network", route: "/events", icon: "CalendarDays" },
      { key: "explore_curation", label: "Explore content curation", description: "See how you curate and distribute shared content", route: "/exchange", icon: "Eye" },
    ],
  },
  {
    id: "chapter_management",
    label: "Set up chapter structure",
    description: "Define org hierarchy, packaging, and billing responsibility",
    icon: "Users",
    quickWins: [
      { key: "add_child_org", label: "Add a member organization", description: "Create or invite a chapter to your network", route: "/association", icon: "UserPlus" },
      { key: "explore_packaging", label: "Explore packaging", description: "See how baseline plans flow to member orgs", route: "/association", icon: "Package" },
      { key: "view_billing_responsibility", label: "View billing settings", description: "See how costs are shared across the network", route: "/billing", icon: "CreditCard" },
    ],
  },
  {
    id: "governance",
    label: "Explore governance controls",
    description: "Module management, capability flags, and network policies",
    icon: "Globe",
    quickWins: [
      { key: "explore_modules", label: "Explore module management", description: "See how to enable/disable features for chapters", route: "/admin", icon: "Globe" },
      { key: "explore_capabilities", label: "View capability flags", description: "See granular controls available at the network level", route: "/admin", icon: "Eye" },
      { key: "view_admin_controls", label: "Review admin controls", description: "See association-level administration tools", route: "/admin", icon: "Building2" },
    ],
  },
];

/** Get goals based on trial type */
export function getGoalsForTrialType(trialType: "org" | "association" | null): TrialGoal[] {
  if (trialType === "association") return ASSOCIATION_TRIAL_GOALS;
  return TRIAL_GOALS;
}
/** Lightweight activation milestones tracked during trial */
export const TRIAL_MILESTONES: TrialMilestone[] = [
  { key: "goal_selected", label: "Chose a starting goal", description: "Selected what you want to explore first" },
  { key: "first_action", label: "Completed first action", description: "Took your first meaningful step" },
  { key: "viewed_dashboard", label: "Explored the dashboard", description: "Reviewed your organization overview" },
  { key: "created_object", label: "Created something", description: "Added a member, event, or communication", autoDetect: { table: "members", minCount: 1 } },
  { key: "explored_portal", label: "Previewed member experience", description: "Saw what your members will see" },
];

/** Session storage key for trial orientation state */
export const TRIAL_ORIENTATION_KEY = "memberos_trial_orientation";

export interface TrialOrientationState {
  /** "demo" = explore with sample data, "clean" = start fresh, null = not yet chosen */
  trialMode: TrialMode;
  selectedGoalId: string | null;
  completedQuickWins: string[];
  completedMilestones: string[];
  orientationComplete: boolean;
  /** Whether demo data has been purged (transition to real setup) */
  demoPurged: boolean;
  startedAt: string;
}

export function getTrialOrientationState(): TrialOrientationState | null {
  try {
    const raw = sessionStorage.getItem(TRIAL_ORIENTATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setTrialOrientationState(state: TrialOrientationState): void {
  sessionStorage.setItem(TRIAL_ORIENTATION_KEY, JSON.stringify(state));
}

export function initTrialOrientationState(): TrialOrientationState {
  const state: TrialOrientationState = {
    trialMode: null,
    selectedGoalId: null,
    completedQuickWins: [],
    completedMilestones: [],
    orientationComplete: false,
    demoPurged: false,
    startedAt: new Date().toISOString(),
  };
  setTrialOrientationState(state);
  return state;
}

/** Demo data entity types that can be seeded & purged (order matters for FK safety) */
export const DEMO_ENTITY_TYPES = [
  "chamber_event_sponsors",
  "chamber_event_check_ins",
  "chamber_event_registrations",
  "connection_outcomes",
  "connection_directives",
  "committee_members",
  "value_events",
  "renewal_risks",
  "hot_deals",
  "event_speakers",
  "sponsorship_subscriptions",
  "sponsorship_tiers",
  "email_campaigns",
  "committees",
  "chamber_events",
  "members",
  "companies",
] as const;

export type DemoEntityType = (typeof DEMO_ENTITY_TYPES)[number];

/**
 * Demo data tag used on all seeded demo records.
 * Records with tags containing this value are considered demo data.
 */
export const DEMO_DATA_TAG = "__demo__";
